import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import {
  ChannelEnvelopeReviewService,
  type ChannelAssayComparison,
  type ChannelEnvelopeReviewOut,
  type ChannelQaFinding,
  type ChannelSnapshotV2 as ChannelSnapshot,
} from '@/api'
import {
  channelSnapshotForApi,
  channelSnapshotsEqual,
  cloneChannelSnapshot,
} from '@/lib/extractionsV2ChannelSnapshot'
import {
  clearEnvelopeReviewSessions,
  createEnvelopeReviewLifecycle,
  envelopeReviewLoadHooks,
  releaseEnvelopeReviewSession,
  type EnvelopeReviewLoadFailure,
  type EnvelopeReviewSaveFailure,
} from '@/stores/envelopeReviewLifecycle'

type ChannelLinkedAssay = ChannelEnvelopeReviewOut['linked_assay']

export type ChannelEnvelopeLoadFailure = EnvelopeReviewLoadFailure

export interface ChannelEnvelopeReviewSession {
  baseline: ChannelSnapshot | null
  working: ChannelSnapshot | null
  floorDiagrams?: ChannelEnvelopeReviewOut['floor_diagrams']
  qaFindings: ChannelQaFinding[]
  assayComparison: ChannelAssayComparison | null
  linkedAssay: ChannelLinkedAssay | null
  originalContentHash: string
  effectiveContentHash: string
  reviewIdentityHash: string
  corrected: boolean
  markedDone: boolean
  loading: boolean
  saving: boolean
  resetting: boolean
  completing: boolean
  conflict: boolean
  writable: boolean
  error: string | null
  completionError: string | null
  completionFailure: EnvelopeReviewSaveFailure | null
  saveFailure: EnvelopeReviewSaveFailure | null
  loadFailure: ChannelEnvelopeLoadFailure | null
  requestGeneration: number
  authoritativeRevision: number
}

function reviewKey(projectId: number, fileId: string): string {
  return `${projectId}:${fileId}`
}

function cloneReviewValue<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

function emptySession(): ChannelEnvelopeReviewSession {
  return {
    baseline: null,
    working: null,
    floorDiagrams: null,
    qaFindings: [],
    assayComparison: null,
    linkedAssay: null,
    originalContentHash: '',
    effectiveContentHash: '',
    reviewIdentityHash: '',
    corrected: false,
    markedDone: false,
    loading: false,
    saving: false,
    resetting: false,
    completing: false,
    conflict: false,
    writable: false,
    error: null,
    completionError: null,
    completionFailure: null,
    saveFailure: null,
    loadFailure: null,
    requestGeneration: 0,
    authoritativeRevision: 0,
  }
}

function applyReview(
  session: ChannelEnvelopeReviewSession,
  review: ChannelEnvelopeReviewOut,
): void {
  session.baseline = cloneChannelSnapshot(review.snapshot)
  session.working = cloneChannelSnapshot(review.snapshot)
  session.floorDiagrams = cloneReviewValue(review.floor_diagrams ?? null)
  session.qaFindings = cloneReviewValue(review.qa_findings)
  session.assayComparison = cloneReviewValue(review.assay_comparison)
  session.linkedAssay = cloneReviewValue(review.linked_assay)
  session.originalContentHash = review.original_content_hash
  session.effectiveContentHash = review.effective_content_hash
  session.reviewIdentityHash = review.review_identity_hash
  session.corrected = review.corrected
  session.markedDone = review.marked_done
  session.writable = review.writable
  session.conflict = false
  session.error = null
  session.completionError = null
  session.completionFailure = null
  session.saveFailure = null
  session.loadFailure = null
  session.authoritativeRevision += 1
}

export const useChannelEnvelopeReviewsStore = defineStore('channelEnvelopeReviews', () => {
  const sessions = ref<Map<string, ChannelEnvelopeReviewSession>>(new Map())

  function ensureSession(projectId: number, fileId: string): ChannelEnvelopeReviewSession {
    const key = reviewKey(projectId, fileId)
    let session = sessions.value.get(key)
    if (!session) {
      sessions.value.set(key, emptySession())
      session = sessions.value.get(key)
    }
    if (!session) throw new Error('The channel review session could not be initialized.')
    return session
  }

  function sessionFor(projectId: number, fileId: string) {
    const session = ensureSession(projectId, fileId)
    return computed(() => session)
  }

  function dirtyFor(projectId: number, fileId: string) {
    const session = ensureSession(projectId, fileId)
    return computed(() => !channelSnapshotsEqual(session.baseline, session.working))
  }

  const lifecycle = createEnvelopeReviewLifecycle<
    ChannelSnapshot,
    ChannelEnvelopeReviewOut,
    ChannelEnvelopeReviewSession
  >({
    applyReview,
    cloneSnapshot: cloneChannelSnapshot,
    conflictCode: 'channel_review_conflict',
    ensureSession,
    getReview: (projectId, fileId) =>
      ChannelEnvelopeReviewService.getReviewProjectsProjectIdChannelEnvelopesFilesFileIdReviewGet({
        projectId,
        fileId,
      }),
    saveReview: (projectId, fileId, requestBody) =>
      ChannelEnvelopeReviewService.saveReviewProjectsProjectIdChannelEnvelopesFilesFileIdReviewPut({
        projectId,
        fileId,
        requestBody: {
          ...requestBody,
          snapshot: channelSnapshotForApi(requestBody.snapshot),
        },
      }),
    resetReview: (projectId, fileId, requestBody) =>
      ChannelEnvelopeReviewService.resetReviewProjectsProjectIdChannelEnvelopesFilesFileIdReviewResetPost(
        {
          projectId,
          fileId,
          requestBody,
        },
      ),
    setReviewCompletion: (projectId, fileId, requestBody) =>
      ChannelEnvelopeReviewService.setReviewCompletionProjectsProjectIdChannelEnvelopesFilesFileIdReviewCompletionPut(
        {
          projectId,
          fileId,
          requestBody,
        },
      ),
    snapshotsEqual: channelSnapshotsEqual,
    ...envelopeReviewLoadHooks<ChannelSnapshot, ChannelEnvelopeReviewSession>(),
  })

  function clear(): void {
    clearEnvelopeReviewSessions(sessions.value)
  }

  function release(projectId: number, fileId: string): void {
    releaseEnvelopeReviewSession(
      sessions.value,
      reviewKey(projectId, fileId),
      channelSnapshotsEqual,
    )
  }

  return {
    sessionFor,
    dirtyFor,
    load: lifecycle.load,
    replaceWorking: lifecycle.replaceWorking,
    save: lifecycle.save,
    reset: lifecycle.reset,
    setCompletion: lifecycle.setCompletion,
    reloadAfterConflict: lifecycle.reloadAfterConflict,
    release,
    clear,
  }
})
